import { FC } from 'react';
import {
  faCircleUser,
  faTag,
  faArrowsTurnRight,
} from '@fortawesome/pro-regular-svg-icons';
import { useLocation, Link } from 'react-router-dom';
import { BoxV2 as Box } from 'portal-commons';

import { CampaignDetail } from '@/apis/core';
import RcsSectionBlock from './RcsSectionBlock';
import InfoItemLayout from './InfoItemLayout';
import InfoItem from './InfoItem';
import { BRAND_RELATIONSHIP_SCORE } from '@/constants';
import { ROUTES } from '@/constants/paths';

const CspBrandResellerSection: FC<{
  campaign: CampaignDetail;
  verticalTypes: Record<string, string>;
}> = ({ campaign, verticalTypes }) => {
  const { brand, reseller } = campaign;
  const { brandRelationshipScore } = brand;
  const csp = brand?.csp;
  const brandRelation = BRAND_RELATIONSHIP_SCORE.find(
    (item) => item.value === brandRelationshipScore
  )?.label;
  const location = useLocation();

  return (
    <RcsSectionBlock icon={faCircleUser} title="CSP Details" showDivider={true}>
      <Box data-testid="campaignCspDetails">
        <InfoItemLayout.Vertical>
          <InfoItemLayout.Horizontal>
            <InfoItem title="CSP Legal Name">{csp?.companyName}</InfoItem>
            <InfoItem title="CSP ID">{csp?.uid}</InfoItem>
          </InfoItemLayout.Horizontal>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Email Address">{csp?.email}</InfoItem>
            <InfoItem title="Phone Number">{csp?.phone}</InfoItem>
          </InfoItemLayout.Horizontal>
        </InfoItemLayout.Vertical>
      </Box>
      <Box sx={{ my: 'l' }}>
        <RcsSectionBlock.Body
          title="Brand Details"
          icon={faTag}
          showDivider
          data-testid="campaignBrandDetails"
        >
          <InfoItemLayout.Vertical>
            <InfoItemLayout.Horizontal>
              <InfoItem title="Brand Legal Name">
                {brand?.entityType === 'SOLE_PROPRIETOR'
                  ? 'Redacted due to PII'
                  : brand?.companyName}
              </InfoItem>
              <InfoItem title="Brand ID">
                {brand?.uid && (
                  <Link
                    to={{
                      pathname: `${ROUTES.BRANDS}/${brand?.uid}`,
                      state: {
                        goBackPage: `${location.pathname}?${location.search}`,
                      },
                    }}
                    style={{ color: '#0091B3', textDecoration: 'underline' }}
                  >
                    {brand?.uid}
                  </Link>
                )}
              </InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem title="Country">{brand?.country}</InfoItem>
              <InfoItem title="State">{brand?.state}</InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem title="EIN">{brand?.ein}</InfoItem>
              <InfoItem title="EIN Issuing Country">
                {brand?.einIssuingCountry}
              </InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem title="Stock Symbol">{brand?.stockSymbol}</InfoItem>
              <InfoItem title="Stock Exchange">{brand?.stockExchange}</InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem title="Website/Online Presence">
                {brand?.website}
              </InfoItem>
              <InfoItem title="Brand Relationship">{brandRelation}</InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem title="Support Phone Number">{brand?.phone}</InfoItem>
              <InfoItem title="Vertical">
                {brand?.vertical &&
                  (verticalTypes[brand?.vertical] || brand?.vertical)}
              </InfoItem>
            </InfoItemLayout.Horizontal>
            <InfoItemLayout.Horizontal>
              <InfoItem title="Political Committee Locale">
                {brand?.optionalAttributes?.politicalCommitteeLocale}
              </InfoItem>
              <InfoItem title="Government Entity">
                {brand?.optionalAttributes?.governmentEntity ? 'True' : 'False'}
              </InfoItem>
            </InfoItemLayout.Horizontal>
          </InfoItemLayout.Vertical>
        </RcsSectionBlock.Body>
      </Box>
      <RcsSectionBlock.Body
        title="Reseller Details"
        icon={faArrowsTurnRight}
        showDivider
        data-testid="campaignResellerDetails"
      >
        <InfoItemLayout.Vertical>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Reseller Legal Name">
              {reseller?.companyName}
            </InfoItem>
            <InfoItem title="Reseller ID">{reseller?.uid}</InfoItem>
          </InfoItemLayout.Horizontal>
          <InfoItemLayout.Horizontal>
            <InfoItem title="Email">{reseller?.email}</InfoItem>
            <InfoItem title="Phone Number">{reseller?.phone}</InfoItem>
          </InfoItemLayout.Horizontal>
        </InfoItemLayout.Vertical>
      </RcsSectionBlock.Body>
    </RcsSectionBlock>
  );
};

export default CspBrandResellerSection;
